import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import type {
  FullComparisonResponse,
  YardiMetroResponse,
  HighRiskMetrosResponse,
} from '../types/yardi';
// NOTE: Components using these hooks should get market name from useActiveMarket()
// import { useActiveMarket } from '../store/useMarketStore';

// =============================================================================
// Types
// =============================================================================

interface YardiComparisonOptions {
  metro: string;
  tier: 'Tier1' | 'Tier2' | 'Tier3';
  enabled?: boolean;
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Fetches Yardi Matrix market benchmark comparison for a specific metro and tier.
 *
 * @param options - Metro name, tier, and enabled flag
 * @returns React Query result with FullComparisonResponse
 *
 * @example
 * ```tsx
 * const market = useActiveMarket();
 * const { data, isLoading, error } = useYardiComparison({
 *   metro: market.display_name,
 *   tier: 'Tier2',
 *   enabled: true
 * });
 * ```
 */
export const useYardiComparison = ({ metro, tier, enabled = true }: YardiComparisonOptions) => {
  return useQuery({
    queryKey: ['yardi-comparison', metro, tier],
    queryFn: async () => {
      const { data } = await api.get<FullComparisonResponse>(
        `/market-benchmarks/comparison/${metro}`,
        {
          params: { tier },
        }
      );
      return data;
    },
    enabled: enabled && !!metro && !!tier,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

/**
 * Fetches Yardi Matrix metro data for a specific metro.
 *
 * @param metro - Metro name
 * @param enabled - Optional flag to enable/disable the query
 * @returns React Query result with YardiMetroResponse
 *
 * @example
 * ```tsx
 * const market = useActiveMarket();
 * const { data, isLoading, error } = useYardiMetro(market.display_name, true);
 * ```
 */
export const useYardiMetro = (metro: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['yardi-metro', metro],
    queryFn: async () => {
      const { data } = await api.get<YardiMetroResponse>(
        `/market-benchmarks/yardi/${metro}`
      );
      return data;
    },
    enabled: enabled && !!metro,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Fetches the list of high-risk metros based on Yardi Matrix criteria.
 *
 * @returns React Query result with HighRiskMetrosResponse
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useHighRiskMetros();
 * ```
 */
export const useHighRiskMetros = () => {
  return useQuery({
    queryKey: ['high-risk-metros'],
    queryFn: async () => {
      const { data } = await api.get<HighRiskMetrosResponse>(
        '/market-benchmarks/high-risk-metros'
      );
      return data;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
};
